/** Generates a unique request id for an outbound submission. */
export function generateRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "req-".concat(Date.now());
}

/** Converts a date or datetime field value to the ISO format the EMR expects. */
function toIsoFieldValue(value, fieldType) {
  if (fieldType === "date") return "".concat(value, "T00:00:00Z");
  if (fieldType === "datetime") return "".concat(value, ":00Z");
  return value;
}

/** Resolves the selected document-kind option from the config. */
export function getDocOption(config, docKind) {
  var _config$fields$find;
  if (!config || !docKind) return undefined;
  return (_config$fields$find = config.fields.find(function (field) {
    return field.type === "type_selector";
  })) === null || _config$fields$find === void 0 || (_config$fields$find = _config$fields$find.options) === null || _config$fields$find === void 0 ? void 0 : _config$fields$find.find(function (option) {
    return option.value === docKind;
  });
}

/** Builds the encounter portion of the payload from filled fields. */
function buildEncounterResource(config, meta, formData, base) {
  var payload = {};
  config.fields.forEach(function (field) {
    var value = formData[field.name];
    if (value) payload[field.name] = toIsoFieldValue(value, field.type);
  });
  return {
    request_id: base.requestId,
    workspace_id: base.workspaceId,
    patient_id: String(base.patientId),
    created_by: base.createdBy,
    resource_type: meta.resourceType,
    action: meta.action,
    payload: payload
  };
}

/** Builds the document portion of the payload from the expanded accordion fields. */
function buildDocResource(config, meta, option, docKind, formData, encounter, base) {
  var _option$fields;
  var payload = {};
  var encounterRecord = encounter;
  var hasData = false;
  ((_option$fields = option.fields) !== null && _option$fields !== void 0 ? _option$fields : []).forEach(function (field) {
    if (field.type === "dynamic") {
      if (field.value_key && encounterRecord) {
        var resolved = encounterRecord[field.value_key];
        if (resolved != null && resolved !== "") payload[field.name] = String(resolved);
      } else if (field.value != null) {
        payload[field.name] = field.value;
      }
      return;
    }
    if (field.type === "file_upload") {
      var s3Key = formData["".concat(docKind, ".").concat(field.name)];
      if (s3Key) {
        payload.file_s3_key = s3Key;
        var contentType = formData["".concat(docKind, ".").concat(field.name, "__ct")];
        if (contentType) payload.content_type = contentType;
        hasData = true;
      }
      return;
    }
    var value = formData["".concat(docKind, ".").concat(field.name)];
    if (!value) return;
    payload[field.name] = toIsoFieldValue(value, field.type);
    hasData = true;
    if (field.name === "loinc_code" && field.options_ref) {
      var _config$dropdowns$fie;
      var match = ((_config$dropdowns$fie = config.dropdowns[field.options_ref]) !== null && _config$dropdowns$fie !== void 0 ? _config$dropdowns$fie : []).find(function (item) {
        return item.value === value;
      });
      if (match) payload.loinc_display = match.label;
    }
  });
  if (!hasData) return null;
  payload.doc_kind = docKind;
  return {
    request_id: base.requestId,
    workspace_id: base.workspaceId,
    patient_id: String(base.patientId),
    created_by: base.createdBy,
    resource_type: meta.resourceType,
    action: meta.action,
    payload: payload
  };
}

/** Arguments for {@link buildSubmissionPayload}. */

/** Builds a single or composite outbound payload from the form state. */
export function buildSubmissionPayload(args) {
  var base = {
    requestId: args.requestId,
    workspaceId: args.workspaceId,
    patientId: args.patientId,
    createdBy: args.createdBy
  };
  var encounterData = args.action === "create" && args.encounterConfig && args.encounterMeta ? buildEncounterResource(args.encounterConfig, args.encounterMeta, args.formData, base) : null;
  var option = getDocOption(args.docConfig, args.docKind);
  var docData = option && args.docConfig && args.docMeta && args.docKind ? buildDocResource(args.docConfig, args.docMeta, option, args.docKind, args.formData, args.encounter, base) : null;
  if (encounterData && docData) return {
    encounter: encounterData,
    doc: docData
  };
  if (encounterData) return encounterData;
  if (docData) return docData;
  return null;
}

/** Returns the labels of required encounter fields that are not yet filled. */
export function missingRequiredFields(config, formData) {
  if (!config) return [];
  return config.fields.filter(function (field) {
    return field.type !== "dynamic" && field.required && !formData[field.name];
  }).map(function (field) {
    return field.label;
  });
}

/** Returns the labels of required fields for the selected document kind that are not yet filled. */
export function missingRequiredDocFields(config, docKind, formData) {
  var _option$fields2;
  var option = getDocOption(config, docKind);
  if (!option || !docKind) return [];
  return ((_option$fields2 = option.fields) !== null && _option$fields2 !== void 0 ? _option$fields2 : []).filter(function (field) {
    return field.type !== "dynamic" && field.required && !formData["".concat(docKind, ".").concat(field.name)];
  }).map(function (field) {
    return field.label;
  });
}